import { useState, useEffect, useCallback } from 'react';

export const useThrottle = (key: string, delayMs: number) => {
  const [remainingTime, setRemainingTime] = useState(0);
  const [isThrottled, setIsThrottled] = useState(false);

  const getLastActionTime = useCallback(() => {
    const stored = sessionStorage.getItem(`throttle_${key}`);
    return stored ? parseInt(stored, 10) : 0;
  }, [key]);

  const updateLastActionTime = useCallback(() => {
    const now = Date.now();
    sessionStorage.setItem(`throttle_${key}`, now.toString());
  }, [key]);

  const checkThrottleStatus = useCallback(() => {
    const lastActionTime = getLastActionTime();
    const now = Date.now();
    const timeSinceLastAction = now - lastActionTime;
    
    if (timeSinceLastAction < delayMs) {
      const remaining = Math.ceil((delayMs - timeSinceLastAction) / 1000);
      setRemainingTime(remaining);
      setIsThrottled(true);
      return false;
    } else {
      setRemainingTime(0);
      setIsThrottled(false);
      return true;
    }
  }, [delayMs, getLastActionTime]);

  useEffect(() => {
    checkThrottleStatus();
    
    const interval = setInterval(() => {
      checkThrottleStatus();
    }, 1000);

    return () => clearInterval(interval);
  }, [checkThrottleStatus]);

  const executeAction = useCallback((action: () => void) => {
    if (!isThrottled) {
      updateLastActionTime();
      setIsThrottled(true);
      setRemainingTime(Math.ceil(delayMs / 1000));
      action();
    }
  }, [isThrottled, updateLastActionTime, delayMs]);

  return {
    isThrottled,
    remainingTime,
    executeAction,
  };
};